import React, { useState, useEffect, lazy, Suspense } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
// @ts-ignore
import "./App.css";
import Navbar from "./components/Navbar/Navbar";
import MobileTabBar from "./components/MobileTabBar/MobileTabBar";
import WhatsAppButton from "./components/WhatsAppButton/WhatsAppButton";
import CookieConsentBanner from "./components/CookieConsentBanner/CookieConsentBanner";
import Footer from "./components/Footer/Footer";
import ServiceAreaLinks from "./components/ServiceAreaLinks/ServiceAreaLinks";
import CallbackPopup from "./components/CallbackPopup/CallbackPopup";
import useBottomBarsSpace from "./hooks/useBottomBarsSpace";
import { trackPageView } from "./analytics";
import Home from "./pages/Home/Home";
import NotFoundPage from "./pages/NotFoundPage/NotFoundPage";

// Route-level code splitting for independent service pages.
// Home and the 404 fallback stay on the main bundle (LCP + smallest page);
// everything else below is a route chunk, preloaded by index.tsx on direct
// entry so the first commit is still atomic (no fallback flash / CLS).
const ServicesPage = lazy(() => import("./pages/ServicesPage/ServicesPage"));
const FleetPage = lazy(() => import("./pages/FleetPage/FleetPage"));
const AirportsPage = lazy(() => import("./pages/AirportsPage/AirportsPage"));
const AirportTransferPage = lazy(() => import("./pages/AirportTransferPage/AirportTransferPage"));
const BookNow = lazy(() => import("./pages/BookNow/BookNow"));
const Policies = lazy(() => import("./pages/Policies/Policies"));
const TermsAndConditionsPage = lazy(() => import("./pages/TermsAndConditionsPage/TermsAndConditionsPage"));
const Contact = lazy(() => import("./pages/Contact/Contact"));
const CorporateTravelPage = lazy(() => import("./pages/CorporateTravelPage/CorporateTravelPage"));
const WeddingsPage = lazy(() => import("./pages/WeddingsPage/WeddingsPage"));
const HourlyPage = lazy(() => import("./pages/HourlyPage/HourlyPage"));
const AboutPage = lazy(() => import("./pages/AboutPage/AboutPage"));
const NewsPage = lazy(() => import("./pages/NewsPage/NewsPage"));
const PartyBusPage = lazy(() => import("./pages/PartyBusPage/PartyBusPage"));
const AnniversariesPage = lazy(() => import("./pages/AnniversariesPage/AnniversariesPage"));
const BachelorettePartyPage = lazy(() => import("./pages/BachelorettePartyPage/BachelorettePartyPage"));
const BirthdaysPage = lazy(() => import("./pages/BirthdaysPage/BirthdaysPage"));
const ConcertsPage = lazy(() => import("./pages/ConcertsPage/ConcertsPage"));
const CruisePortPage = lazy(() => import("./pages/CruisePortPage/CruisePortPage"));
const DestinationPage = lazy(() => import("./pages/DestinationPage/DestinationPage"));
const FuneralsPage = lazy(() => import("./pages/FuneralsPage/FuneralsPage"));
const GroupEventsPage = lazy(() => import("./pages/GroupEventsPage/GroupEventsPage"));
const NightOnTheTownPage = lazy(() => import("./pages/NightOnTheTownPage/NightOnTheTownPage"));
const PromPage = lazy(() => import("./pages/PromPage/PromPage"));
const SchoolEventsPage = lazy(() => import("./pages/SchoolEventsPage/SchoolEventsPage"));
const SportsEventsPage = lazy(() => import("./pages/SportsEventsPage/SportsEventsPage"));
const WineAndSightseeingPage = lazy(() => import("./pages/WineAndSightseeingPage/WineAndSightseeingPage"));
const ServiceAreaPage = lazy(() => import("./pages/ServiceAreaPage/ServiceAreaPage"));
const ServiceAreasPage = lazy(() => import("./pages/ServiceAreasPage/ServiceAreasPage"));

function RouteLoadingFallback(): JSX.Element {
  return (
    <div
      style={{
        padding: "4rem 1rem",
        textAlign: "center",
        color: "#A0A0A0",
      }}
    >
      <div className="spinner-border text-secondary" role="status" style={{ width: "2rem", height: "2rem" }} />
      <p style={{ marginTop: "1rem", fontSize: "0.95rem" }}>Loading page…</p>
    </div>
  );
}

// Phase 3A CLS fix. On the very first render of a lazy route, React.lazy always
// throws a pending promise (its module resolution completes one microtask
// later), and React commits the Suspense fallback for that initial-mount
// suspension — which visibly replaces the react-snap prerendered page for one
// frame (measured CLS 0.51–0.82). index.tsx preloads the entry route's chunk
// and exposes the already-resolved page module as window.__gsInitialPage; for
// that route only, we render the real component directly so the first commit
// is the full page (atomic swap, like the main-bundle routes). Everything else
// — lazy splitting, the Suspense boundary, client-side navigation fallbacks —
// is unchanged.
function gsRouteElement(path: string, LazyPage: React.ComponentType): JSX.Element {
  const boot = (window as { __gsInitialPage?: { path?: string; component?: React.ComponentType } }).__gsInitialPage;
  if (boot && boot.path === path && typeof boot.component === "function") {
    const DirectPage = boot.component;
    return <DirectPage />;
  }
  return <LazyPage />;
}

function ScrollToTop(): null {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const target = document.getElementById(decodeURIComponent(hash.slice(1)));
      if (target) {
        target.scrollIntoView({ behavior: "smooth", block: "start" });
        return;
      }
    }
    window.scrollTo(0, 0);
  }, [pathname, hash]);

  return null;
}

const CALLBACK_SEEN_KEY = "gs-callback-seen-2";

function AppContent(): JSX.Element {
  const [showCallback, setShowCallback] = useState<boolean>(false);
  const { pathname } = useLocation();

  // Reserve room at the end of the page for the cookie banner, so the footer's
  // copyright line is never buried underneath it.
  useBottomBarsSpace();

  useEffect(() => {
    setShowCallback(false);
    // Skip while react-snap pre-renders: the popup markup must not be baked
    // into the snapshot (showCallback is false on hydration).
    if (navigator.webdriver || typeof (window as any).snapSaveState === "function") {
      return;
    }
    // Pages that already carry a lead form (or legal copy) don't need the
    // callback prompt on top of them.
    if (["/get-a-quote", "/contact", "/privacy-policy", "/terms-and-conditions"].includes(pathname)) {
      return;
    }
    // Once per browser session, only after the visitor has had time to read.
    try {
      if (window.sessionStorage.getItem(CALLBACK_SEEN_KEY)) return;
    } catch {
      // Storage blocked (private mode) — show it anyway.
    }
    const timer = setTimeout(() => {
      try {
        window.sessionStorage.setItem(CALLBACK_SEEN_KEY, "1");
      } catch {
        // Ignore storage failures; the popup still works for this page view.
      }
      setShowCallback(true);
    }, 8000);
    return () => clearTimeout(timer);
  }, [pathname]);

  useEffect(() => {
    trackPageView(pathname);
  }, [pathname]);

  return (
    <>
      <ScrollToTop />
      <a className="skip-link" href="#main-content">
        Skip to main content
      </a>
      <Navbar />
      <MobileTabBar />
      <main id="main-content" tabIndex={-1}>
        <Suspense fallback={<RouteLoadingFallback />}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/services/corporate-travel" element={gsRouteElement("/services/corporate-travel", CorporateTravelPage)} />
            <Route path="/services/weddings" element={gsRouteElement("/services/weddings", WeddingsPage)} />
            <Route path="/services/hourly-as-directed" element={gsRouteElement("/services/hourly-as-directed", HourlyPage)} />
            <Route path="/about" element={gsRouteElement("/about", AboutPage)} />
            <Route path="/news" element={gsRouteElement("/news", NewsPage)} />
            <Route path="/services/party-bus" element={gsRouteElement("/services/party-bus", PartyBusPage)} />
            <Route path="/services/anniversaries" element={gsRouteElement("/services/anniversaries", AnniversariesPage)} />
            <Route path="/services/bachelorette-party" element={gsRouteElement("/services/bachelorette-party", BachelorettePartyPage)} />
            <Route path="/services/birthdays" element={gsRouteElement("/services/birthdays", BirthdaysPage)} />
            <Route path="/services/concerts" element={gsRouteElement("/services/concerts", ConcertsPage)} />
            <Route path="/services/cruise-port" element={gsRouteElement("/services/cruise-port", CruisePortPage)} />
            <Route path="/services/destination" element={gsRouteElement("/services/destination", DestinationPage)} />
            <Route path="/services/funerals" element={gsRouteElement("/services/funerals", FuneralsPage)} />
            <Route path="/services/group-events" element={gsRouteElement("/services/group-events", GroupEventsPage)} />
            <Route path="/services/night-on-the-town" element={gsRouteElement("/services/night-on-the-town", NightOnTheTownPage)} />
            <Route path="/services/prom" element={gsRouteElement("/services/prom", PromPage)} />
            <Route path="/services/school-events" element={gsRouteElement("/services/school-events", SchoolEventsPage)} />
            <Route path="/services/sports-events" element={gsRouteElement("/services/sports-events", SportsEventsPage)} />
            <Route path="/services/wine-and-sightseeing" element={gsRouteElement("/services/wine-and-sightseeing", WineAndSightseeingPage)} />
            <Route path="/services" element={gsRouteElement("/services", ServicesPage)} />
            <Route path="/service-areas" element={gsRouteElement("/service-areas", ServiceAreasPage)} />
            <Route
              path="/limo-service/:slug"
              element={gsRouteElement("/limo-service", ServiceAreaPage)}
            />
            <Route path="/fleet" element={gsRouteElement("/fleet", FleetPage)} />
            <Route path="/airports" element={gsRouteElement("/airports", AirportsPage)} />
            <Route
              path="/services/airport-transportation/:slug"
              element={gsRouteElement("/services/airport-transportation", AirportTransferPage)}
            />
            <Route path="/get-a-quote" element={gsRouteElement("/get-a-quote", BookNow)} />
            <Route path="/privacy-policy" element={gsRouteElement("/privacy-policy", Policies)} />
            <Route path="/policies" element={<Navigate to="/privacy-policy" replace />} />
            <Route
              path="/terms-and-conditions"
              element={gsRouteElement("/terms-and-conditions", TermsAndConditionsPage)}
            />
            <Route path="/contact" element={gsRouteElement("/contact", Contact)} />
            {/* Legacy indexed URLs -> current routes (client-side backup to .htaccess 301s). */}
            <Route path="/terms-conditions" element={<Navigate to="/terms-and-conditions" replace />} />
            <Route path="/services/airport-transportation/albany-alb" element={<Navigate to="/services/airport-transportation/alb" replace />} />
            <Route path="/services/airport-transportation/philadelphia-phl" element={<Navigate to="/services/airport-transportation/phl" replace />} />
            <Route path="/corporate-transportation" element={<Navigate to="/services/corporate-travel" replace />} />
            <Route path="/ewr" element={<Navigate to="/services/airport-transportation/ewr" replace />} />
            <Route path="/ewr-airport-limo" element={<Navigate to="/services/airport-transportation/ewr" replace />} />
            <Route path="/airport-limo-nj" element={<Navigate to="/airports" replace />} />
            <Route path="/acy" element={<Navigate to="/services/airport-transportation/acy" replace />} />
            <Route path="/lga" element={<Navigate to="/services/airport-transportation/lga" replace />} />
            <Route path="/phl" element={<Navigate to="/services/airport-transportation/phl" replace />} />
            <Route path="/alb" element={<Navigate to="/services/airport-transportation/alb" replace />} />
            <Route path="/jfk" element={<Navigate to="/services/airport-transportation/jfk" replace />} />
            <Route path="/services/airport-transportation" element={<Navigate to="/airports" replace />} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </main>
      <ServiceAreaLinks />
      <Footer />
      <WhatsAppButton />
      <CookieConsentBanner />
      {showCallback && <CallbackPopup onClose={() => setShowCallback(false)} />}
    </>
  );
}

export default function App(): JSX.Element {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}
